import { Button } from '@ui/components/ui/button';
import {
  ApproveSprintFormValues,
  DisputeSprintFormValues,
  SprintProductEntity,
  SprintProductParamsType,
  SprintProductStatus,
  useApproveSprintProduct,
  useDisputeSprintProduct,
} from '@data';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ApprovalPopup,
  ApprovalSummaryButton,
  DisputePopup,
  DisputeSummaryButton,
  hasDispute,
  hasSettledRemark,
  useApprovedBy,
  useRemarkAnsweredBy,
} from '@app/components/core';
import { useHoldPrompts } from '@app/components/core/layout/promptQueue';
import DisputeAnsweredDialog from './DisputeAnsweredDialog';
import { hasSeenDisputeAnswer, markDisputeAnswerSeen } from './disputeAnswerSeen';

// Approve the sprint report or dispute it, and — once decided — show who decided and when, the button
// opening the decision itself: what was said and who it reached.
const ApproveAction = ({ sprintProduct }: { sprintProduct: SprintProductEntity }): ReactElement => {
  const { t } = useTranslation();
  const { sprintId, productId } = useParams() as Pick<SprintProductParamsType, 'sprintId' | 'productId'>;
  const [searchParams, setSearchParams] = useSearchParams();

  // Only while the report is still theirs to answer: approving clears `disputeResolution`.
  const hasUnseenAnswer =
    sprintProduct.status === SprintProductStatus.closed &&
    hasSettledRemark(sprintProduct) &&
    !!sprintProduct.disputeResolution &&
    !hasSeenDisputeAnswer(sprintProduct.id, sprintProduct.disputeResolvedAt);

  // Read once, not per render, or cancelling the dialog would reopen it immediately.
  const [cameToApprove] = useState(() => searchParams.get('approve') === '1');
  const canApprove = sprintProduct.status === SprintProductStatus.closed && !hasDispute(sprintProduct);

  // Held back while an answer is unread, or they would approve without reading it.
  const [open, setOpen] = useState(() => cameToApprove && canApprove && !hasUnseenAnswer);
  // Cleared once read, so a cancelled dialog stays cancelled across a reload.
  useEffect(() => {
    if (!searchParams.has('approve')) return;

    const remaining = new URLSearchParams(searchParams);
    remaining.delete('approve');
    setSearchParams(remaining, { replace: true });
  }, [searchParams, setSearchParams]);

  const [disputeOpen, setDisputeOpen] = useState(false);
  const { mutate: approveSprintProduct, isPending: isApproving } = useApproveSprintProduct();
  const { mutate: disputeSprintProduct, isPending: isDisputing } = useDisputeSprintProduct();

  const handleApprove = (values: ApproveSprintFormValues) => {
    approveSprintProduct(values, { onSuccess: () => setOpen(false) });
  };

  const handleDispute = (values: DisputeSprintFormValues) => {
    disputeSprintProduct(values, { onSuccess: () => setDisputeOpen(false) });
  };

  const isApproved =
    sprintProduct.status === SprintProductStatus.approved || sprintProduct.status === SprintProductStatus.invoiced;

  const isDisputed = hasDispute(sprintProduct);

  const approvedBy = useApprovedBy(sprintProduct);
  const answeredBy = useRemarkAnsweredBy(sprintProduct);

  const [showAnswer, setShowAnswer] = useState(hasUnseenAnswer);

  // Whatever the mail's button brought them here to answer goes first. Anything we raise off our own
  // bat — the set-password prompt — waits until they are done with it.
  //
  // Every dialog on this report, not only the approval: reading an answer can lead straight into
  // approving or into writing another remark, and a prompt that appeared between those steps would land
  // on top of the one the client had just chosen.
  useHoldPrompts(open || showAnswer || disputeOpen || isApproving || isDisputing);

  // Marked on the way out rather than on the way in, so a reload before it is read shows it again. The
  // answer's own instant goes with it: a later answer on the same report is news again.
  const closeAnswer = (): void => {
    markDisputeAnswerSeen(sprintProduct.id, sprintProduct.disputeResolvedAt);
    setShowAnswer(false);
  };

  return (
    <>
      {/* One answer per report. Once it has been given, this reads back what was decided rather than
          offering the choice again: a report waiting on a remark is not waiting on the client. */}
      {isDisputed && <DisputeSummaryButton sprintProduct={sprintProduct} />}
      {sprintProduct.status === SprintProductStatus.closed && !isDisputed && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="inline-flex items-center" onClick={() => setDisputeOpen(true)}>
            {t('actions.dispute')}
          </Button>
          <Button className="inline-flex items-center" onClick={() => setOpen(true)}>
            {t('actions.approve')}
          </Button>
        </div>
      )}
      {isApproved && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <ApprovalSummaryButton sprintProduct={sprintProduct} />
          <span>{approvedBy}</span>
        </div>
      )}
      {showAnswer && (
        <DisputeAnsweredDialog
          sprintProduct={sprintProduct}
          answeredBy={answeredBy}
          onApprove={() => {
            closeAnswer();
            setOpen(true);
          }}
          onDisputeAgain={() => {
            closeAnswer();
            setDisputeOpen(true);
          }}
          onClose={() => {
            closeAnswer();
            if (cameToApprove && canApprove) setOpen(true);
          }}
        />
      )}
      {open && (
        <ApprovalPopup
          sprintProduct={sprintProduct}
          onClose={() => setOpen(false)}
          approveSprintProduct={handleApprove}
          isApproving={isApproving}
          productId={productId}
          sprintId={sprintId}
        />
      )}
      {disputeOpen && (
        <DisputePopup
          sprintProduct={sprintProduct}
          onClose={() => setDisputeOpen(false)}
          disputeSprintProduct={handleDispute}
          isDisputing={isDisputing}
          productId={productId}
          sprintId={sprintId}
        />
      )}
    </>
  );
};

export default ApproveAction;
